import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import { useTheme } from '@material-ui/core/styles';
import { mdiCheck, mdiClose, mdiMagnify, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import Spinner from 'components/Spinner/Spinner';
import TextInput from 'components/TextInput/TextInput';
import { BankBottomSheet } from 'scenes/ProjectBank/components/BankBottomSheet/BankBottomSheet';
import {
  selectGroupedProjectMediaTags,
  selectIsCreatingOrUpdatingTags,
  selectSelectedFilepermissionMediaTags,
  selectSelectedProjectMediaTags,
} from 'store/mediaTags/selectors/mediaTags.selectors';
import { addMediaTagForFile } from 'store/mediaTags/thunks/addMediaTagForFile.thunk';
import { createProjectMediaTag } from 'store/mediaTags/thunks/createProjectMediaTag.thunk';
import {
  VIEWER_ADD_TAG_SHEET_ID,
  filterMediaTagOptions,
  findMediaTagByName,
  getMediaTagLabel,
  getViewerSheetZIndex,
  groupMediaTagOptions,
  orderMediaTagsForRow,
} from './mobileMediaTags';
import { useKeyboardInset } from './useKeyboardInset';

const TITLE_ID = `${VIEWER_ADD_TAG_SHEET_ID}-title`;
const FIELD_ID = `${VIEWER_ADD_TAG_SHEET_ID}-field`;

const EMPTY_ARRAY = [];

/** A tag of the project, as opposed to an assignment no tag is made of yet. */
const isTagOption = (option) => option.type === 'MEDIA_TAG';

/** The glyph cell of the create and "already added" rows. */
const ROW_ICON_STYLE = { minWidth: 36 };

/**
 * What the sheet holds, mounted while it is open (and while it slides out),
 * so the lists of tags and assignments are only read then.
 */
function AddTagSheetContent({ open, onClose }) {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  // The project's tags and the assignments no tag is made of yet, grouped,
  // less the photo's own tags.
  const options = useSelector(selectGroupedProjectMediaTags) || EMPTY_ARRAY;
  const projectTags =
    useSelector(selectSelectedProjectMediaTags) || EMPTY_ARRAY;
  const fileTags = useSelector(selectSelectedFilepermissionMediaTags);
  const isPending = Boolean(useSelector(selectIsCreatingOrUpdatingTags));
  const keyboardInset = useKeyboardInset();
  const [query, setQuery] = useState('');

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // Opened again before it had slid out, the sheet still holds what it held;
  // it starts from an empty field all the same.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const name = query.trim();
  const photoTags = useMemo(() => orderMediaTagsForRow(fileTags), [fileTags]);
  const shownOptions = useMemo(
    () => filterMediaTagOptions(options, query),
    [options, query]
  );
  const groups = useMemo(
    () => groupMediaTagOptions(shownOptions),
    [shownOptions]
  );

  // What is typed, by name: on the photo already, a tag to add, an
  // assignment to make a tag of, or a new tag. A tag of that name on the
  // photo stops the other three.
  const onPhoto = findMediaTagByName(photoTags, name);
  const exactTag = onPhoto
    ? undefined
    : findMediaTagByName(options.filter(isTagOption), name);
  const exactAssignment = onPhoto
    ? undefined
    : findMediaTagByName(
        options.filter((option) => !isTagOption(option)),
        name
      );
  const canCreate =
    Boolean(name) &&
    !onPhoto &&
    !exactAssignment &&
    !findMediaTagByName(projectTags, name);

  // The sheet closes once the tag is on the photo. A failure keeps it open,
  // and the slice's snackbar says so.
  const run = async (action) => {
    if (isPending) return;
    const result = await dispatch(action);
    if (!result.error && mountedRef.current) onClose();
  };

  const pickOption = (option) =>
    run(
      isTagOption(option)
        ? addMediaTagForFile(option.id)
        : // An assignment: the tag is made of it, as the desktop field does.
          createProjectMediaTag({
            name: option.name,
            assignmentId: option.id,
            assignmentNumberPath: option.assignmentNumberPath,
          })
    );

  const createTag = () => run(createProjectMediaTag({ name }));

  // Enter takes the tag of that name, else the assignment, else a new tag;
  // with nothing to take, it only puts the keyboard away.
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (isPending) return;
    if (exactTag) pickOption(exactTag);
    else if (exactAssignment) pickOption(exactAssignment);
    else if (canCreate) createTag();
    else event.target.blur();
  };

  const surface = theme.component.projectBankSurface.backgroundColor;

  return (
    // All the height the sheet allows, asked for with a basis past it, so
    // the list has room under the field and the sheet does not grow as it
    // is filtered.
    <div
      data-viewer-add-tag-body="true"
      style={{
        flex: '1 1 100vh',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Grid
        container
        alignItems="center"
        wrap="nowrap"
        style={{
          flexShrink: 0,
          gap: theme.spacing(1),
          padding: theme.spacing(1, 1, 0, 2),
        }}
      >
        <Grid item xs zeroMinWidth>
          <h2
            id={TITLE_ID}
            style={{
              margin: 0,
              fontFamily: theme.typography.fontPanton.fontFamily,
              fontSize: '1rem',
              fontWeight: 'normal',
              lineHeight: 1.3,
              color: theme.palette.text.primary,
            }}
          >
            {t('gallery.add_tag')}
          </h2>
        </Grid>
        {isPending ? (
          <Grid item style={{ flexShrink: 0 }}>
            <Spinner />
          </Grid>
        ) : null}
        <Grid item style={{ flexShrink: 0 }}>
          <IconButton
            aria-label={t('common.close')}
            title={t('common.close')}
            data-viewer-add-tag-close="true"
            onClick={onClose}
          >
            <Icon path={mdiClose} size={0.8} />
          </IconButton>
        </Grid>
      </Grid>
      {/* Near the top, so the iOS keyboard does not cover it. Not focused
          on opening: the keyboard would hide the list, half of what the
          sheet is for. */}
      <div style={{ flexShrink: 0, padding: theme.spacing(1, 2) }}>
        <TextInput
          id={FIELD_ID}
          fullWidth
          rounded
          shrink
          label={t('gallery.tag')}
          placeholder={t('gallery.search_or_create_tag')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          inputProps={{
            autoComplete: 'off',
            // "Valmis" on the keyboard; in lower case, as React 16 does not
            // know the property.
            enterkeyhint: 'done',
            // 16px or more keeps iOS from zooming the page in on the field.
            style: { fontSize: 16 },
            'data-viewer-tag-search': 'true',
            onKeyDown: handleKeyDown,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon path={mdiMagnify} size={0.8} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t('bank.clear_search')}
                  title={t('bank.clear_search')}
                  style={{ padding: 4 }}
                  onClick={() => setQuery('')}
                >
                  <Icon path={mdiClose} size={0.7} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </div>
      {/* Scrolls by itself, and pads its end by the keyboard's height, so
          its last rows can be scrolled up out from under the keyboard. No
          box between it and the sheet may clip sideways or be absolutely
          positioned (see BankBottomSheet). */}
      <List
        disablePadding
        data-viewer-tag-options="true"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          paddingBottom:
            keyboardInset > 0
              ? keyboardInset + 8
              : 'calc(8px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {canCreate ? (
          <ListItem
            button
            disabled={isPending}
            data-viewer-create-tag="true"
            onClick={createTag}
          >
            <ListItemIcon style={ROW_ICON_STYLE}>
              <Icon path={mdiPlus} size={0.8} />
            </ListItemIcon>
            <ListItemText primary={t('gallery.create_tag', { name })} />
          </ListItem>
        ) : null}
        {onPhoto ? (
          <ListItem disabled data-viewer-tag-already-added="true">
            <ListItemIcon style={ROW_ICON_STYLE}>
              <Icon path={mdiCheck} size={0.8} />
            </ListItemIcon>
            <ListItemText
              primary={getMediaTagLabel(onPhoto)}
              secondary={t('gallery.tag_already_added')}
            />
          </ListItem>
        ) : null}
        {groups.map(({ group, options: groupOptions }) => (
          <li key={group} data-viewer-tag-group={group}>
            <ul style={{ padding: 0 }}>
              <ListSubheader
                style={{ lineHeight: '36px', backgroundColor: surface }}
              >
                {group}
              </ListSubheader>
              {groupOptions.map((option) => (
                <ListItem
                  key={option.id}
                  button
                  disabled={isPending}
                  data-viewer-tag-option={option.id}
                  onClick={() => pickOption(option)}
                >
                  <ListItemText primary={getMediaTagLabel(option)} />
                </ListItem>
              ))}
            </ul>
          </li>
        ))}
        {/* Only a project without tags: with every tag of it on this photo
            already there is simply nothing more to pick. */}
        {!name && options.length === 0 && projectTags.length === 0 ? (
          <ListItem data-viewer-no-project-tags="true">
            <ListItemText secondary={t('gallery.no_project_tags')} />
          </ListItem>
        ) : null}
      </List>
    </div>
  );
}

AddTagSheetContent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Adds a tag to the photo in the viewer on a phone-sized screen, opened by
 * the '+' chip of the tag row (MobileMediaTagRow). What the desktop's tag
 * field (MediaTagInput) does, in a sheet that takes the height a phone
 * leaves it: a field to search or name a tag, near the top where the iOS
 * keyboard leaves it in sight, over a list that scrolls. The list shows, as
 * far as the field allows:
 * 1. "Luo tunniste ”…”" while no tag of the project and no assignment has
 *    the typed name: a new free-form tag;
 * 2. "Tunniste on jo lisätty", greyed, while the photo has a tag of that
 *    name;
 * 3. the project's tags the photo does not have and the assignments no tag
 *    is made of yet, in their groups; a tag goes on the photo, and an
 *    assignment is made into a tag on it, as on the desktop;
 * 4. with nothing typed and nothing to list, a hint that a name makes the
 *    project's first tag.
 * Enter takes the tag of the typed name, else the assignment, else a new
 * tag.
 *
 * The bank's bottom sheet (BankBottomSheet), over the viewer, which is a
 * dialog (getViewerSheetZIndex), and named by its title. It closes by a
 * swipe down, a tap above it, Escape or its X, and once the tag is on the
 * photo; a failure keeps it open, and the slice's snackbar reports it.
 * While a tag is being made or added, a spinner shows and the rows wait.
 * Every opening starts from an empty field.
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 */
export function MobileAddMediaTagSheet({ open, onClose }) {
  const theme = useTheme();

  return (
    <BankBottomSheet
      id={VIEWER_ADD_TAG_SHEET_ID}
      open={open}
      onClose={onClose}
      labelledBy={TITLE_ID}
      surface="base"
      zIndex={getViewerSheetZIndex(theme)}
      data-viewer-add-tag-sheet="true"
    >
      <AddTagSheetContent open={open} onClose={onClose} />
    </BankBottomSheet>
  );
}

MobileAddMediaTagSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MobileAddMediaTagSheet;
